import { SIP_DOMAIN, SIP_PROTOCOL } from '@shared/config/sip';
import type { DialPadTarget } from '@shared/ui/dial-pad';

import { parseBridgeCallTarget } from './call-target-parser';

/**
 * Находит подсказки для dial-pad по локальному lookup списку.
 * Ищет по display-name, aor и login (user-part).
 */
export const findDialPadSuggestions = ({
	query,
	lookupCalls,
	limit = DEFAULT_SUGGESTIONS_LIMIT,
}: FindDialPadSuggestionsParams): DialPadTarget[] => {
	const normalizedQuery = normalizeUserInput(query);
	if (!normalizedQuery || !lookupCalls?.length || limit <= 0) {
		return [];
	}

	const rank = (target: DialPadTarget) => {
		const displayName = normalizeValue(target.displayName || '');
		const aor = normalizeValue(target.aor || '');
		const login = toUser(target.aor || '');

		if (displayName === normalizedQuery || login === normalizedQuery || aor === normalizedQuery) {
			return 0;
		}

		if (
			displayName.startsWith(normalizedQuery) ||
			login.startsWith(normalizedQuery) ||
			aor.startsWith(normalizedQuery)
		) {
			return 1;
		}

		return 2;
	};

	const matched = lookupCalls.filter((call) => {
		const displayName = normalizeValue(call.displayName || '');
		const aor = normalizeValue(call.aor || '');
		const login = toUser(call.aor || '');

		return (
			displayName.includes(normalizedQuery) ||
			aor.includes(normalizedQuery) ||
			login.includes(normalizedQuery)
		);
	});

	const uniqByAor = new Map<string, DialPadTarget>();
	matched
		.sort((a, b) => {
			const rankDiff = rank(a) - rank(b);
			if (rankDiff !== 0) return rankDiff;

			return normalizeValue(a.displayName || a.aor).localeCompare(
				normalizeValue(b.displayName || b.aor)
			);
		})
		.forEach((item) => {
			const key = normalizeValue(item.aor || '');
			if (!key || uniqByAor.has(key) || uniqByAor.size >= limit) {
				return;
			}
			uniqByAor.set(key, item);
		});

	return [...uniqByAor.values()];
};

export interface FindDialPadSuggestionsParams {
	query: string;
	lookupCalls?: DialPadTarget[];
	limit?: number;
}

const SIP_SCHEME_REGEXP = /^sip:/i;
const TEL_SCHEME_REGEXP = /^tel:/i;
const DEFAULT_SUGGESTIONS_LIMIT = 10;

const toBase = (target: string) =>
	target.replace(SIP_SCHEME_REGEXP, '').split('?')[0].split(';')[0].toLowerCase();

const toDomain = (target: string) => toBase(target).split('@')[1] || '';
const normalizeValue = (value: string) => value.toLowerCase();
const normalizeUserInput = (value: string) => value.trim().toLowerCase();

const toAor = (target: string, fallbackDomain: string) => {
	const base = toBase(target);
	if (!base) return '';
	return base.includes('@')
		? `${SIP_PROTOCOL}:${base}`
		: `${SIP_PROTOCOL}:${base}@${fallbackDomain}`;
};

const toUser = (target: string) => toBase(target).split('@')[0] || '';

const resolvePhoneTarget = (target: string): DialPadTarget | null => {
	const normalizedTarget = target.trim();
	if (!TEL_SCHEME_REGEXP.test(normalizedTarget) && !normalizedTarget.startsWith('+')) {
		return null;
	}

	const aor = parseBridgeCallTarget(normalizedTarget);
	return { aor, displayName: aor };
};

/**
 * Резолвит итоговый SIP target из raw-строки dial pad.
 * Поддерживает short id, user@domain, sip:user@domain и Name-Addr формат.
 */
export const resolveDialPadTarget = ({
	rawInput,
	lookupCalls,
	selfSipAor,
}: ResolveDialPadTargetParams): DialPadTarget => {
	const trimmedRawInput = rawInput.trim();
	if (!trimmedRawInput) return { aor: '', displayName: '' };

	const phoneTarget = resolvePhoneTarget(trimmedRawInput);
	if (phoneTarget) return phoneTarget;

	const uriMatch =
		trimmedRawInput.match(/<(sip:[^>]+)>/i) || trimmedRawInput.match(/(sip:[^\s;>]+)/i);
	const displayNameMatch =
		trimmedRawInput.match(/^\s*"((?:\\.|[^"\\])*)"\s*</) ||
		trimmedRawInput.match(/^\s*([^"<]+?)\s*</);
	const sourceTarget = uriMatch?.[1] ?? trimmedRawInput;
	const fallbackDomain = toDomain(selfSipAor || '') || SIP_DOMAIN;
	const normalizedInputAor = toAor(sourceTarget, fallbackDomain);
	const hasDomainInInput = toBase(sourceTarget).includes('@');
	const inputUser = toUser(sourceTarget);
	const normalizedRawInput = normalizeUserInput(trimmedRawInput);

	const matchedCall = lookupCalls?.find((call) => {
		const sameAor = toAor(call.aor, fallbackDomain) === normalizedInputAor;
		const sameDisplayName = normalizeValue(call.displayName || '') === normalizedRawInput;
		const sameUser = !hasDomainInInput && toUser(call.aor) === inputUser;
		return sameAor || sameDisplayName || sameUser;
	});

	const resolvedAor =
		!hasDomainInInput && matchedCall
			? toAor(matchedCall.aor, fallbackDomain) || normalizedInputAor
			: normalizedInputAor;

	const parsedDisplayName =
		displayNameMatch?.[1]?.replaceAll('\\"', '"').replaceAll('\\\\', '\\') || '';
	const fallbackDisplayName = toUser(resolvedAor);
	const resolvedDisplayName = matchedCall?.displayName || parsedDisplayName || fallbackDisplayName;

	return { aor: resolvedAor, displayName: resolvedDisplayName };
};

export interface ResolveDialPadTargetParams {
	rawInput: string;
	lookupCalls?: DialPadTarget[];
	selfSipAor?: string;
}
